import { ExternalLink, Search, X } from '../../../components/icon/solar-icons.js'

import { getInitialsFromName } from '../../../views/portal/utils/format.js'
import { t } from '../../../../common/i18n.js'
import type { NotionColor } from '../../../../common/types/notion.js'
import type { NotionProject } from '../../../../common/types/notion.js'
import type { Language } from '../../../../common/types/i18n.js'
import { PageHeader } from '../../../components/page-header/page-header.js'
import { PageShell } from '../../../components/page-shell/page-shell.js'
import { ProjectCard } from '../../../components/project-card/project-card.js'
import { Spinner } from '../../../components/spinner/spinner.js'
import type { View } from '../../../types.js'
import { ViewBase, ViewBaseProps } from '../../base/base.js'
import './worktime.css'

// ── Props interface ─────────────────────────────────────────────────────────

type ActiveTab = 'mine' | 'all'

export interface WorktimeViewProps {
    view: View
    lang: Language
    isPublicProfile?: boolean
    userName?: string

    // Data
    loading: boolean
    loadError: string | null
    filteredProjects: NotionProject[]
    projectsByYear: Map<string, NotionProject[]>
    sortedYears: string[]

    // UI state
    activeTab: ActiveTab
    searchQuery: string

    // Modal state
    selectedProject: NotionProject | null

    // Callbacks
    onSetActiveTab: (tab: ActiveTab) => void
    onSetSearchQuery: (query: string) => void
    onProjectClick: (project: NotionProject) => void
    onCloseModal: () => void
}

// ── Date formatting helpers ─────────────────────────────────────────────────

const formatDate = (start: string | null, end: string | null, lang: Language) => {
    if (!start) return t.playtime.worktimeView.dateTba(lang)
    const locale = lang === 'et' ? 'et-EE' : 'en-GB'
    const startDate = new Date(start).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
    if (!end || start === end) return startDate
    const endDate = new Date(end).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
    return `${startDate} → ${endDate}`
}

// ── Status color mapping ────────────────────────────────────────────────────

import { hexBadgeStyle } from '../../../../common/utils/badge-color.js'

const statusColorMap: Record<string, string> = {
    default: '#e0e0e0',
    gray: '#9e9e9e',
    brown: '#795548',
    orange: '#ff9800',
    yellow: '#ffeb3b',
    green: '#4caf50',
    blue: '#2196f3',
    purple: '#9c27b0',
    pink: '#e91e63',
    red: '#f44336',
}

const getStatusColor = (color: NotionColor | null): string =>
    statusColorMap[color ?? 'default'] ?? statusColorMap.default

// ── Main component (pure render) ────────────────────────────────────────────

export const WorktimeView = (props: WorktimeViewProps & { base?: ViewBaseProps }) => {
    const { lang } = props
    const wt = t.playtime.worktimeView

    const content = (
        <>

            {/* Action bar: search + mine/all toggle on one row */}
            <div className="worktime__action-bar">
                <div className="worktime__action-bar-left">
                    <div className="worktime__search-wrapper">
                        <Search size={16} className="worktime__search-icon" />
                        <input
                            className="worktime__search"
                            type="text"
                            placeholder={wt.searchProjects(lang)}
                            value={props.searchQuery}
                            onChange={(e) => props.onSetSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="worktime__action-bar-right">
                    {!props.isPublicProfile && (
                        <div className="worktime__tab-toggle">
                            <button
                                type="button"
                                className={`worktime__toggle-btn ${props.activeTab === 'mine' ? 'worktime__toggle-btn--active' : ''}`}
                                onClick={() => props.onSetActiveTab('mine')}
                            >
                                {wt.myProjects(lang)}
                            </button>
                            <button
                                type="button"
                                className={`worktime__toggle-btn ${props.activeTab === 'all' ? 'worktime__toggle-btn--active' : ''}`}
                                onClick={() => props.onSetActiveTab('all')}
                            >
                                {wt.allProjects(lang)}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Error state */}
            {props.loadError && (
                <div className="portal-card__error">
                    <span>{props.loadError}</span>
                </div>
            )}

            {props.loading ? (
                <Spinner />
            ) : props.filteredProjects.length === 0 ? (
                <div className="worktime__empty">
                    <p>{wt.noProjects(lang)}</p>
                </div>
            ) : (
                <div className="worktime__timeline">
                    {props.sortedYears.map((year) => (
                        <section key={year} className="worktime__year-section">
                            <h2 className="worktime__year-title">{year}</h2>
                            <div className="worktime__projects">
                                {props.projectsByYear.get(year)!.map((project) => (
                                    <ProjectCard
                                        key={`${project.id}-${year}`}
                                        project={project}
                                        lang={lang}
                                        onClick={() => props.onProjectClick(project)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {/* Project Detail Modal */}
            {props.selectedProject && (
                <div
                    className="worktime-modal__overlay"
                    onClick={() => props.onCloseModal()}
                    onKeyDown={(e) => { if (e.key === 'Escape') props.onCloseModal() }}
                    tabIndex={-1}
                    ref={(el) => el?.focus()}
                >
                    <div
                        className="worktime-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="worktime-modal__close"
                            onClick={() => props.onCloseModal()}
                            aria-label={wt.closeProjectDetails(lang)}
                        >
                            <X size={24} />
                        </button>

                        <div className="worktime-modal__body">
                            <div className="worktime-modal__header">
                                <div className="worktime-modal__title-row">
                                    <h2 className="worktime-modal__title">
                                        {props.selectedProject.name}
                                        {props.selectedProject.notionUrl && (
                                            <a
                                                href={props.selectedProject.notionUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="worktime-modal__notion-link"
                                                aria-label={wt.viewInNotion(lang)}
                                            >
                                                <ExternalLink size={18} />
                                                <span className="worktime-modal__notion-tip">
                                                    {wt.viewInNotion(lang)}
                                                </span>
                                            </a>
                                        )}
                                    </h2>
                                </div>

                                <div className="worktime-modal__meta">
                                    {props.selectedProject.status && (
                                        <span
                                            className="worktime-modal__status-badge"
                                            style={
                                                hexBadgeStyle(
                                                    getStatusColor(
                                                        props.selectedProject.statusColor,
                                                    ),
                                                ) ?? undefined
                                            }
                                        >
                                            {props.selectedProject.status}
                                        </span>
                                    )}
                                    <span className="worktime-modal__date">
                                        {formatDate(
                                            props.selectedProject.startDate,
                                            props.selectedProject.endDate,
                                            lang,
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Team members */}
                            {props.selectedProject.team.length > 0 && (
                                <div className="worktime-modal__section">
                                    <h3 className="worktime-modal__section-title">
                                        {wt.team(lang)}
                                    </h3>
                                    <div className="worktime-modal__team-list">
                                        {props.selectedProject.owner && (
                                            <div className="worktime-modal__team-member">
                                                {props.selectedProject.owner.avatarUrl ? (
                                                    <img
                                                        className="worktime-modal__team-avatar"
                                                        src={props.selectedProject.owner.avatarUrl}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="worktime-modal__team-avatar worktime-modal__team-avatar--placeholder">
                                                        {getInitialsFromName(
                                                            props.selectedProject.owner.name,
                                                        )}
                                                    </div>
                                                )}
                                                <span className="worktime-modal__team-name">
                                                    {props.selectedProject.owner.name}
                                                </span>
                                                <span className="worktime-modal__team-role">
                                                    {wt.owner(lang)}
                                                </span>
                                            </div>
                                        )}
                                        {props.selectedProject.team
                                            .filter(
                                                (m) =>
                                                    m.email !==
                                                    props.selectedProject!.owner?.email,
                                            )
                                            .map((member) => (
                                                <div
                                                    key={member.notionUserId}
                                                    className="worktime-modal__team-member"
                                                >
                                                    {member.avatarUrl ? (
                                                        <img
                                                            className="worktime-modal__team-avatar"
                                                            src={member.avatarUrl}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <div className="worktime-modal__team-avatar worktime-modal__team-avatar--placeholder">
                                                            {getInitialsFromName(member.name)}
                                                        </div>
                                                    )}
                                                    <span className="worktime-modal__team-name">
                                                        {member.name}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Testimonial — chat bubble + the client contact who gave it */}
                            {props.selectedProject.testimonial && (
                                <div className="worktime-modal__testimonial">
                                    <div className="worktime-modal__testimonial-bubble">
                                        <p className="worktime-modal__testimonial-text">
                                            &ldquo;{props.selectedProject.testimonial}&rdquo;
                                        </p>
                                    </div>
                                    {(props.selectedProject.testimonialAuthor?.name ||
                                        props.selectedProject.testimonialAuthor?.client) && (
                                        <div className="worktime-modal__testimonial-author">
                                            <span className="worktime-modal__testimonial-author-name">
                                                {props.selectedProject.testimonialAuthor.name ??
                                                    props.selectedProject.testimonialAuthor.client}
                                            </span>
                                            {(() => {
                                                const author =
                                                    props.selectedProject.testimonialAuthor
                                                const detail = [
                                                    author.role,
                                                    author.name ? author.client : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ')
                                                return detail ? (
                                                    <span className="worktime-modal__testimonial-author-detail">
                                                        {detail}
                                                    </span>
                                                ) : null
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Curated description (Notion 'Hub summary' property) */}
                            {props.selectedProject.hubSummary && (
                                <div className="worktime-modal__content">
                                    <p className="worktime-modal__summary">
                                        {props.selectedProject.hubSummary}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )

    if (props.isPublicProfile) {
        return (
            <ViewBase {...(props.base ?? { notifications: undefined, navigation: undefined })}>
                <PageShell>
                    <PageHeader
                        title={props.userName
                            ? props.userName.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                            : t.playtime.hero.worktimeTitle(lang)
                        }
                    />
                    {content}
                </PageShell>
            </ViewBase>
        )
    }

    return content
}
